import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTheme } from "next-themes";
import { LogOut, Moon, Sun, Menu, ShieldAlert, Eye, UserRoundX } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { Button } from "@/components/ui/button";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { UserAvatar } from "@/components/common/Bits";
import { ViewAsDialog } from "@/components/shell/ViewAsDialog";
import { NotificationBell } from "@/components/shell/NotificationBell";

export default function TopBar({ onMenu }) {
  const { user, can, logout, readOnly, startImpersonation } = useAuth();
  const { theme, setTheme } = useTheme();
  const navigate = useNavigate();
  const [confirmLogout, setConfirmLogout] = useState(false);
  const [viewAs, setViewAs] = useState(false);

  const forceLogout = async () => {
    try {
      await api.post("/auth/force-logout-all");
      toast.success("Every user has been signed out. Your session was renewed.");
      setConfirmLogout(false);
      window.location.reload();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };

  return (
    <header className="flex h-12 shrink-0 items-center gap-3 border-b border-border bg-card px-3 md:px-4" data-testid="top-bar">
      <Button variant="ghost" size="icon" className="lg:hidden" onClick={onMenu} data-testid="mobile-menu-btn"><Menu className="h-4 w-4" /></Button>
      <button onClick={() => navigate("/tickets")} className="flex items-center gap-2" data-testid="brand-link">
        <span className="grid h-6 w-6 place-items-center bg-primary text-[11px] font-black text-primary-foreground mono">TT</span>
        <span className="mono text-xs font-semibold uppercase tracking-[0.2em]">Total Tickets</span>
      </button>
      <div className="ml-2 flex flex-1 items-center gap-3 overflow-hidden">
        <span className="hidden text-[11px] text-muted-foreground md:block">{user.view_only ? "view only" : ""}</span>
      </div>
      <div className="flex items-center gap-1">
        {can("system.force_logout") && !readOnly && (
          <Tooltip>
            <TooltipTrigger asChild>
              <Button variant="outline" size="sm" className="hidden border-red-500/40 text-red-500 hover:bg-red-500/10 hover:text-red-500 sm:inline-flex" onClick={() => setConfirmLogout(true)} data-testid="force-logout-all-btn">
                <UserRoundX className="mr-1.5 h-3.5 w-3.5" /> Force logout all
              </Button>
            </TooltipTrigger>
            <TooltipContent>Immediately sign every user out</TooltipContent>
          </Tooltip>
        )}
        <NotificationBell />
        <Button variant="ghost" size="icon" onClick={() => setTheme(theme === "dark" ? "light" : "dark")} data-testid="theme-toggle-btn" aria-label="Toggle theme">
          {theme === "dark" ? <Sun className="h-4 w-4" /> : <Moon className="h-4 w-4" />}
        </Button>
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button className="ml-1 flex items-center gap-2 border border-transparent px-1.5 py-1 transition-colors hover:border-border" data-testid="user-menu-btn">
              <UserAvatar name={user.name} src={user.avatar_url} size="sm" />
              <span className="hidden text-xs font-semibold sm:block">{user.name}</span>
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-60 bg-popover">
            <DropdownMenuLabel>
              <div className="text-xs font-semibold">{user.name}</div>
              <div className="mono text-[11px] font-normal text-muted-foreground">{user.email}</div>
              <div className="mt-1 flex items-center gap-1.5">
                <span className="eyebrow">{user.view_only ? "view only" : `${user.permissions?.length || 0} capabilities`}</span>
              </div>
            </DropdownMenuLabel>
            <DropdownMenuSeparator />
            {can("users.impersonate") && !readOnly && (
              <DropdownMenuItem onClick={() => setViewAs(true)} data-testid="view-as-menu-item"><Eye className="mr-2 h-4 w-4" /> View as another user</DropdownMenuItem>
            )}
            {can("system.force_logout") && !readOnly && (
              <DropdownMenuItem className="sm:hidden" onClick={() => setConfirmLogout(true)}><ShieldAlert className="mr-2 h-4 w-4" /> Force logout all</DropdownMenuItem>
            )}
            <DropdownMenuItem onClick={() => logout().then(() => navigate("/login"))} data-testid="logout-menu-item"><LogOut className="mr-2 h-4 w-4" /> Sign out</DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>

      <AlertDialog open={confirmLogout} onOpenChange={setConfirmLogout}>
        <AlertDialogContent data-testid="force-logout-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Force logout of all users?</AlertDialogTitle>
            <AlertDialogDescription>Every active session, on every device, is invalidated immediately. Users must request a new sign-in code. Your own session will be renewed.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="force-logout-cancel-btn">Cancel</AlertDialogCancel>
            <AlertDialogAction className="bg-red-600 hover:bg-red-700" onClick={forceLogout} data-testid="force-logout-confirm-btn">Sign everyone out</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
      <ViewAsDialog open={viewAs} onOpenChange={setViewAs} onPick={async (id) => { const t = await startImpersonation(id); toast.info(`Now viewing as ${t.name} (read-only)`); navigate("/tickets"); }} />
    </header>
  );
}
